import { useTranslation } from "react-i18next";
import { useSubmission } from "../../../shared/state/useSubmission";
import ErrorNotice from "../../../errors/ErrorNotice";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { connectAndReturnHere } from "../../profile/connections/connectProvider";
import type { RepositoryStep } from "./privateRepository";

/** What the person sees about the repository, and the consent a private one needs. */
export function RepositoryStepNotice({ step }: { step: RepositoryStep }) {
  const { t } = useTranslation();
  const current = step.current;
  if (current === null || current.outcome === "public") return null;
  const repository = current.repository ?? "";
  const provider = current.providerName ?? "";
  if (current.outcome === "ready") {
    return (
      <StatusMessage tone="info">
        <span className="block">{t("sources.import.cloud.private.ready", { repository, provider })}</span>
        <OptionLabel className="mt-2">
          <input type="checkbox" checked={step.consented} onChange={(event) => { step.setConsented(event.target.checked); }} />
          {t("sources.import.cloud.private.consent")}
        </OptionLabel>
      </StatusMessage>
    );
  }
  return <StatusMessage tone={current.outcome === "not_configured" ? "info" : "warning"}>{t(`sources.import.cloud.private.${current.outcome}`, { repository, provider })}</StatusMessage>;
}

/** Beside the greyed-out button: what resolves the step from here. */
export function RepositoryStepResolve({ step, onCheckAgain }: { step: RepositoryStep; onCheckAgain: () => void }) {
  const { t } = useTranslation();
  const submission = useSubmission();
  const current = step.current;
  if (current === null || current.provider === null) return null;
  const provider = current.provider;
  const checkAgain = <AppButton size="sm" variant="secondary" onClick={onCheckAgain}>{t("sources.import.cloud.private.checkAgain")}</AppButton>;
  if (current.outcome === "connect" || current.outcome === "reconnect") {
    return (
      <>
        <AppButton size="sm" variant="secondary" disabled={submission.state.status === "submitting"}
          onClick={() => { submission.submit(() => connectAndReturnHere(provider)); }}>
          {t(current.outcome === "connect" ? "sources.import.cloud.private.connectAction" : "sources.import.cloud.private.reconnectAction", { provider: current.providerName ?? provider })}
        </AppButton>
        {checkAgain}
        {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
      </>
    );
  }
  if (current.outcome === "not_visible") {
    return (
      <>
        {current.installationUrl === null ? null : <a className="app-link text-sm underline" href={current.installationUrl} target="_blank" rel="noopener noreferrer">{t("sources.import.cloud.private.installAction")}</a>}
        {checkAgain}
      </>
    );
  }
  return null;
}
